import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Building, Loader2, AlertCircle, Save } from 'lucide-react';

import MultiSelectInput from '@@/components/Inputs/MultiSelectInput';
import {
  useGetMyPropertiesQuery,
  useUpdateMyPropertiesMutation,
} from '@@/services/user';

type PropertyOption = { id: string; label: string };

type FormValues = { properties: PropertyOption[] };

const MyPropertiesForm: React.FC = () => {
  const { data, isLoading, isFetching, error } = useGetMyPropertiesQuery();
  const [updateMyProperties, { isLoading: isSaving }] =
    useUpdateMyPropertiesMutation();

  const options = useMemo<PropertyOption[]>(
    () =>
      data?.properties.map((property) => ({
        id: property.id,
        label: property.name,
      })) ?? [],
    [data],
  );

  // Pas de sélection enregistrée (null) = toutes les résidences
  const savedValues = useMemo<FormValues>(() => {
    const selectedPropertyIds = data?.selectedPropertyIds;
    return {
      properties: selectedPropertyIds
        ? options.filter((option) => selectedPropertyIds.includes(option.id))
        : options,
    };
  }, [data, options]);

  // `values` synchronise le formulaire avec le cache dès le 1er rendu.
  // keepDirtyValues : un refetch n'écrase pas une saisie en cours.
  const { control, handleSubmit, reset, setValue, watch } = useForm<FormValues>(
    {
      values: savedValues,
      resetOptions: { keepDirtyValues: true },
    },
  );

  const selected = watch('properties') ?? [];
  const allSelected = selected.length === options.length;

  // Comparaison ensembliste : l'ordre des chips ne compte pas
  const hasChanges = useMemo(() => {
    const savedIds = new Set(savedValues.properties.map(({ id }) => id));
    return (
      selected.length !== savedIds.size ||
      selected.some(({ id }) => !savedIds.has(id))
    );
  }, [selected, savedValues]);

  const onSubmit = async ({ properties }: FormValues) => {
    const propertyIds = properties.map((property) => property.id);
    try {
      await updateMyProperties({
        propertyIds: propertyIds.length === options.length ? null : propertyIds,
      }).unwrap();
    } catch {
      // Toast d'erreur déjà affiché par rtkQueryErrorHandler
      return;
    }
    // La saisie devient la nouvelle référence, sans attendre le refetch
    reset({ properties });
    toast.success('Votre parc a bien été mis à jour', {
      position: 'top-right',
    });
  };

  const content = (() => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-6 text-neutral-600">
          <Loader2 size={24} className="animate-spin text-primary-600 mr-2" />
          <span className="text-sm sm:text-base">
            Chargement de vos résidences...
          </span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-start space-x-2 text-danger-600">
          <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
          <p className="text-sm sm:text-base">
            Impossible de charger vos résidences. Veuillez réessayer.
          </p>
        </div>
      );
    }

    if (!data || data.properties.length === 0) {
      return (
        <p className="text-neutral-600 text-sm sm:text-base">
          Aucune résidence n'est rattachée à votre partenaire.
        </p>
      );
    }

    return (
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <h3 className="font-semibold text-neutral-900 text-sm sm:text-base">
            Mes résidences
          </h3>
          <p className="text-neutral-600 text-xs sm:text-sm mb-3">
            Choisissez les résidences que vous souhaitez gérer sur Alacaza Pro.
          </p>
          <MultiSelectInput
            id="properties"
            label=""
            ariaLabel="Mes résidences"
            control={control}
            options={options}
            values={selected}
            placeholder="Sélectionnez au moins une résidence"
            error={
              selected.length === 0
                ? {
                    type: 'required',
                    message: 'Sélectionnez au moins une résidence',
                  }
                : undefined
            }
          />
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-xs sm:text-sm text-neutral-500">
            {selected.length} / {options.length} résidence(s) sélectionnée(s)
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              disabled={allSelected}
              onClick={() =>
                setValue('properties', options, { shouldDirty: true })
              }
              className="inline-flex items-center justify-center px-4 py-2 border border-neutral-300 text-neutral-700 hover:bg-neutral-100 font-medium rounded-lg transition-colors duration-200 text-sm sm:text-base w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Tout sélectionner
            </button>
            <button
              type="submit"
              disabled={
                !hasChanges || selected.length === 0 || isSaving || isFetching
              }
              className="inline-flex items-center justify-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm sm:text-base w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Save size={16} className="mr-2" />
              )}
              Enregistrer
            </button>
          </div>
        </div>
      </form>
    );
  })();

  return (
    <section className="bg-white rounded-2xl shadow-lg">
      <div className="bg-gradient-to-r from-primary-50 to-blue-50 px-4 sm:px-6 py-3 sm:py-4 border-b border-primary-100 rounded-t-2xl">
        <div className="flex items-center space-x-3">
          <Building size={18} className="text-primary-600 sm:w-5 sm:h-5" />
          <h2 className="text-lg sm:text-xl font-bold text-neutral-900">
            Mon parc
          </h2>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        <div className="bg-neutral-50 rounded-xl p-4 sm:p-5">{content}</div>
      </div>
    </section>
  );
};

export default MyPropertiesForm;
